import { useEffect, useMemo, useState } from "react";
import { MailPlus, RefreshCcw, Tags, Trash2 } from "lucide-react";
import {
  archiveMailTaskDraft,
  createManualMailTaskDraft,
  getMailTaskDrafts,
  markMailTaskDraftConverted,
  syncGmailTaskDrafts,
  updateMailTaskDraft
} from "../services/mailTaskService";
import {
  createMonthlyProcess,
  DEFAULT_PROCESS_PROJECT_ID
} from "../services/monthlyProcessService";

const normalizeText = (value = "") =>
  String(value)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replaceAll("ı", "i")
    .replaceAll("İ", "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

const getAutoShortName = (companyName = "") =>
  String(companyName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(" ");

const todayInputValue = () => new Date().toISOString().slice(0, 10);

const monthInputValue = () => new Date().toISOString().slice(0, 7);

function MailTasksView({
  user,
  users = [],
  firmaListesi = [],
  processProjects = [],
  isAdmin = false,
  themeMode = "light",
  addActivityLog,
  notifyTeam
}) {
  const isDark = themeMode === "dark";
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncingGmail, setIsSyncingGmail] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [manualForm, setManualForm] = useState({
    subject: "",
    body: "#firma: \n#sorumlu: \n#tarih: \n#öncelik: normal\n#proje: Genel Görev Süreçleri\n\nMail içeriğini buraya yapıştırın...",
    fromEmail: ""
  });
  const [draftEdits, setDraftEdits] = useState({});

  const colors = {
    pageText: isDark ? "#e5edf6" : "#124254",
    muted: isDark ? "#9ca3af" : "#64748b",
    card: isDark ? "#162235" : "#fff",
    card2: isDark ? "#101827" : "#f8fafc",
    border: isDark ? "#26364d" : "#dbe4ef",
    input: isDark ? "#0f172a" : "#fff",
    inputText: isDark ? "#e5edf6" : "#0f172a"
  };

  const activeProjects = useMemo(() => {
    const list = processProjects.length
      ? processProjects
      : [{ id: DEFAULT_PROCESS_PROJECT_ID, name: "Aylık Süreçler" }];

    return list;
  }, [processProjects]);

  const loadDrafts = async () => {
    setIsLoading(true);
    setStatusMessage("");

    try {
      const data = await getMailTaskDrafts({
        currentUserEmail: user?.email || "",
        isAdmin
      });

      setDrafts(data);
      setDraftEdits((current) => {
        const next = { ...current };
        data.forEach((draft) => {
          if (!next[draft.id]) {
            next[draft.id] = buildInitialEdit(draft);
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Mail görev taslakları yüklenemedi:", error);
      setStatusMessage(`Taslaklar yüklenemedi: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, isAdmin]);

  const getCurrentUserName = () =>
    user?.name ||
    user?.displayName ||
    user?.fullName ||
    user?.email ||
    "Kullanıcı";

  const matchCompanyName = (value = "", fallbackText = "") => {
    const search = normalizeText(`${value} ${fallbackText}`);
    if (!search) return "";

    const exactMatch = firmaListesi.find((company) =>
      [company.firmaAdi, company.firmaKisaAdi, company.name, company.shortName]
        .filter(Boolean)
        .some((candidate) => normalizeText(candidate) === normalizeText(value))
    );

    if (exactMatch) return exactMatch.firmaAdi || exactMatch.name || "";

    const fuzzyMatch = firmaListesi.find((company) => {
      const candidates = [company.firmaAdi, company.firmaKisaAdi, company.name, company.shortName].filter(Boolean);
      return candidates.some((candidate) => {
        const normalizedCandidate = normalizeText(candidate);
        return normalizedCandidate && (search.includes(normalizedCandidate) || normalizedCandidate.includes(normalizeText(value)));
      });
    });

    return fuzzyMatch?.firmaAdi || fuzzyMatch?.name || "";
  };

  const matchResponsibleName = (value = "", fallbackEmail = "") => {
    const search = normalizeText(value || fallbackEmail);
    if (!search) return getCurrentUserName();

    const matchedUser = users.find((item) => {
      const candidates = [
        item.name,
        item.displayName,
        item.fullName,
        item.email,
        item.email?.split("@")[0]
      ].filter(Boolean);

      return candidates.some((candidate) => {
        const normalizedCandidate = normalizeText(candidate);
        return normalizedCandidate === search || normalizedCandidate.includes(search) || search.includes(normalizedCandidate);
      });
    });

    return matchedUser?.name || matchedUser?.displayName || matchedUser?.fullName || matchedUser?.email || value || getCurrentUserName();
  };

  const matchProjectId = (value = "") => {
    const search = normalizeText(value);

    if (!search) {
      return activeProjects.find((item) => item.id !== DEFAULT_PROCESS_PROJECT_ID)?.id || DEFAULT_PROCESS_PROJECT_ID;
    }

    const matchedProject = activeProjects.find((item) => {
      const normalizedName = normalizeText(item.name || item.slug || item.id);
      return normalizedName === search || normalizedName.includes(search) || search.includes(normalizedName);
    });

    return matchedProject?.id || activeProjects.find((item) => item.id !== DEFAULT_PROCESS_PROJECT_ID)?.id || DEFAULT_PROCESS_PROJECT_ID;
  };

  const buildStepsFromDraft = (draft, edit) => {
    const customSteps = String(edit.stepsText || "")
      .split(/\r?\n/)
      .map((line) => line.replace(/^[-*•\d.)\s]+/, "").trim())
      .filter(Boolean);

    const steps = customSteps.length
      ? customSteps
      : [
          "Mail incelendi",
          "Gerekli aksiyon alındı",
          "Müşteriye/ilgili kişiye dönüş yapıldı"
        ];

    return steps.map((title, index) => ({
      order: index + 1,
      groupTitle: "Mail Görevi",
      title,
      isRequired: true
    }));
  };

  function buildInitialEdit(draft) {
    const companyName = matchCompanyName(draft.tagCompany, `${draft.subject || ""} ${draft.cleanBody || ""}`);
    const responsibleName = matchResponsibleName(draft.tagResponsible, draft.forwardedByEmail);
    const projectId = matchProjectId(draft.tagProject);

    return {
      title: draft.title || draft.subject || "Mailden gelen görev",
      companyName,
      responsibleName,
      dueDate: draft.tagDueDate || "",
      priority: draft.tagPriority || "normal",
      projectId,
      month: monthInputValue(),
      note: [draft.tagNote, draft.cleanBody].filter(Boolean).join("\n\n").trim(),
      stepsText: (draft.tagSteps || []).join("\n")
    };
  }

  const updateDraftEdit = (draftId, field, value) => {
    setDraftEdits((current) => ({
      ...current,
      [draftId]: {
        ...current[draftId],
        [field]: value
      }
    }));
  };

  const createManualDraft = async () => {
    if (!manualForm.subject.trim() && !manualForm.body.trim()) {
      alert("Mail konusu veya içeriği giriniz.");
      return;
    }

    try {
      await createManualMailTaskDraft({
        subject: manualForm.subject,
        body: manualForm.body,
        fromEmail: manualForm.fromEmail,
        forwardedByEmail: user?.email || "",
        forwardedByName: getCurrentUserName(),
        createdByUid: user?.uid || "",
        createdByEmail: user?.email || "",
        createdByName: getCurrentUserName()
      });

      setManualForm({
        subject: "",
        body: "#firma: \n#sorumlu: \n#tarih: \n#öncelik: normal\n#proje: Genel Görev Süreçleri\n\nMail içeriğini buraya yapıştırın...",
        fromEmail: ""
      });
      setStatusMessage("Mail görev taslağı oluşturuldu.");
      await addActivityLog?.("MAIL_GOREV_TASLAGI_OLUSTURULDU", manualForm.subject || "Mailden gelen görev");
      await loadDrafts();
    } catch (error) {
      alert(`Taslak oluşturulamadı: ${error.message}`);
    }
  };

  const convertDraftToTask = async (draft) => {
    const edit = draftEdits[draft.id] || buildInitialEdit(draft);
    const safeTitle = String(edit.title || "").trim();

    if (!safeTitle) {
      alert("Görev başlığı zorunludur.");
      return;
    }

    if (!edit.responsibleName) {
      alert("Sorumlu seçiniz veya yazınız.");
      return;
    }

    try {
      await updateMailTaskDraft(draft.id, {
        title: safeTitle,
        selectedCompanyName: edit.companyName || "",
        selectedResponsibleName: edit.responsibleName || "",
        selectedProjectId: edit.projectId || "",
        selectedDueDate: edit.dueDate || "",
        selectedPriority: edit.priority || "normal",
        selectedMonth: edit.month || monthInputValue(),
        selectedNote: edit.note || "",
        selectedStepsText: edit.stepsText || ""
      });

      const companyShortName = edit.companyName
        ? (firmaListesi.find((item) => item.firmaAdi === edit.companyName)?.firmaKisaAdi || getAutoShortName(edit.companyName))
        : "";
      const steps = buildStepsFromDraft(draft, edit);
      const createdReference = await createMonthlyProcess({
        companyName: edit.companyName || "",
        companyShortName,
        title: safeTitle,
        month: edit.month || monthInputValue(),
        scheduleType: edit.dueDate ? "range" : "month",
        startDate: edit.dueDate || "",
        endDate: edit.dueDate || "",
        owner: edit.responsibleName,
        processProjectId: edit.projectId || DEFAULT_PROCESS_PROJECT_ID,
        note: edit.note || "",
        source: "mail",
        sourceId: draft.id,
        priority: edit.priority || "normal",
        dueDate: edit.dueDate || "",
        extraFields: {
          mailTaskDraftId: draft.id,
          mailFromEmail: draft.fromEmail || "",
          mailForwardedByEmail: draft.forwardedByEmail || "",
          mailSubject: draft.subject || "",
          mailNotifyAll: Boolean(draft.tagNotifyAll)
        },
        template: {
          id: `mail-${draft.id}`,
          name: safeTitle,
          steps,
          autoRepeatDefault: false,
          recurrence: {
            frequency: "monthly",
            interval: 1,
            createTiming: "periodEnd"
          }
        }
      });

      await markMailTaskDraftConverted({
        draftId: draft.id,
        processId: createdReference?.id || "",
        convertedByEmail: user?.email || ""
      });

      await addActivityLog?.(
        "MAIL_GOREVE_CEVRILDI",
        `${safeTitle} / ${edit.responsibleName}`
      );

      if (draft.tagNotifyAll) {
        await notifyTeam?.({
          title: "Mailden görev oluşturuldu",
          body: `${getCurrentUserName()}, "${safeTitle}" görevini mailden oluşturdu.`,
          type: "task",
          link: "monthlyProcesses",
          processId: createdReference?.id || "",
          processProjectId: edit.projectId || DEFAULT_PROCESS_PROJECT_ID
        });
      }

      setStatusMessage("Taslak göreve çevrildi.");
      await loadDrafts();
    } catch (error) {
      console.error("Mail taslağı göreve çevrilemedi:", error);
      alert(`Görev oluşturulamadı: ${error.message}`);
    }
  };

  const archiveDraft = async (draft) => {
    const confirmed = await window.appConfirm?.(
      "Bu mail görev taslağını arşivlemek istiyor musunuz?",
      { title: "Taslağı arşivle", type: "warning", confirmText: "Arşivle" }
    );

    if (confirmed === false) return;

    await archiveMailTaskDraft(draft.id);
    await loadDrafts();
  };

  const syncGmailInbox = async () => {
    setIsSyncingGmail(true);
    setStatusMessage("Gmail gelen kutusu kontrol ediliyor...");

    try {
      const result = await syncGmailTaskDrafts({ maxResults: 25 });
      const createdCount = Number(result?.createdCount || 0);
      const skippedCount = Number(result?.skippedCount || 0);
      setStatusMessage(`${createdCount} yeni mail görev taslağı oluşturuldu. ${skippedCount} mail daha önce alınmıştı/atlandı.`);
      await loadDrafts();
    } catch (error) {
      console.error("Gmail görevleri senkronize edilemedi:", error);
      setStatusMessage(`Gmail senkronizasyonu başarısız: ${error.message}`);
    } finally {
      setIsSyncingGmail(false);
    }
  };

  const pendingDrafts = drafts.filter((draft) => draft.status !== "converted");
  const convertedDrafts = drafts.filter((draft) => draft.status === "converted");

  const inputStyle = {
    width: "100%",
    borderRadius: 12,
    border: `1px solid ${colors.border}`,
    background: colors.input,
    color: colors.inputText,
    padding: "10px 12px",
    fontWeight: 800,
    boxSizing: "border-box"
  };

  const labelStyle = {
    display: "grid",
    gap: 5,
    fontSize: 12,
    fontWeight: 950,
    color: colors.muted
  };

  const buttonStyle = {
    border: "none",
    borderRadius: 12,
    padding: "10px 13px",
    background: "#0f8f83",
    color: "#fff",
    fontWeight: 950,
    cursor: "pointer"
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: isDark ? "#27364d" : "#e2e8f0",
    color: isDark ? "#e5edf6" : "#124254"
  };

  return (
    <div style={{ color: colors.pageText }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24 }}>Mailden Gelen Görevler</h2>
          <p style={{ margin: "6px 0 0", color: colors.muted, fontWeight: 800 }}>
            Personellerin <strong>gorev@e-calp.com</strong> adresine ilettiği mailleri görev taslağına çevirme ekranı
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={syncGmailInbox}
            disabled={isSyncingGmail}
            style={buttonStyle}
          >
            <MailPlus size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {isSyncingGmail ? "Gmail kontrol ediliyor..." : "Gmail’den Senkronize Et"}
          </button>
          <button type="button" onClick={loadDrafts} disabled={isLoading} style={secondaryButtonStyle}>
            <RefreshCcw size={15} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {isLoading ? "Yükleniyor..." : "Yenile"}
          </button>
        </div>
      </div>

      {statusMessage && (
        <div
          style={{
            borderRadius: 14,
            padding: "11px 13px",
            marginBottom: 14,
            background: statusMessage.includes("başarısız") || statusMessage.includes("yüklenemedi") ? "#fee2e2" : "#dcfce7",
            color: statusMessage.includes("başarısız") || statusMessage.includes("yüklenemedi") ? "#991b1b" : "#166534",
            fontWeight: 900
          }}
        >
          {statusMessage}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 0.9fr) minmax(420px, 1.1fr)", gap: 16 }}>
        <div style={{ display: "grid", gap: 14 }}>
          <section style={{ border: `1px solid ${colors.border}`, background: colors.card, borderRadius: 18, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
              <Tags size={18} />
              <strong>Etiket formatı</strong>
            </div>
            <div style={{ color: colors.muted, fontSize: 12, lineHeight: 1.6, fontWeight: 800 }}>
              Personel maili iletirken en üste şu alanları yazabilir:
            </div>
            <pre
              style={{
                margin: "10px 0 0",
                whiteSpace: "pre-wrap",
                background: colors.card2,
                border: `1px solid ${colors.border}`,
                borderRadius: 14,
                padding: 12,
                color: colors.pageText,
                fontSize: 12,
                lineHeight: 1.5
              }}
            >{`#firma: MCS Yazılım
#sorumlu: Emircan
#tarih: 20.07.2026
#öncelik: yüksek
#proje: Genel Görev Süreçleri
#adım: Mail incelenecek
#adım: Müşteriye dönüş yapılacak
#herkes`}</pre>
          </section>

          <section style={{ border: `1px solid ${colors.border}`, background: colors.card, borderRadius: 18, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
              <MailPlus size={18} />
              <strong>Manuel test taslağı</strong>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <label style={labelStyle}>
                Mail Konusu
                <input
                  value={manualForm.subject}
                  onChange={(event) => setManualForm((current) => ({ ...current, subject: event.target.value }))}
                  placeholder="Fwd: Müşteri talebi..."
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Orijinal Gönderen
                <input
                  value={manualForm.fromEmail}
                  onChange={(event) => setManualForm((current) => ({ ...current, fromEmail: event.target.value }))}
                  placeholder="musteri@firma.com"
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Mail İçeriği
                <textarea
                  value={manualForm.body}
                  onChange={(event) => setManualForm((current) => ({ ...current, body: event.target.value }))}
                  rows={10}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
                />
              </label>
              <button type="button" onClick={createManualDraft} style={buttonStyle}>
                Taslak Oluştur
              </button>
            </div>
          </section>
        </div>

        <section style={{ border: `1px solid ${colors.border}`, background: colors.card, borderRadius: 18, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <div>
              <h3 style={{ margin: 0 }}>Bekleyen Taslaklar</h3>
              <div style={{ marginTop: 4, color: colors.muted, fontWeight: 850, fontSize: 12 }}>
                {pendingDrafts.length} bekleyen · {convertedDrafts.length} göreve çevrilmiş
              </div>
            </div>
          </div>

          {pendingDrafts.length === 0 ? (
            <div style={{ borderRadius: 14, padding: 16, background: colors.card2, color: colors.muted, fontWeight: 850 }}>
              Bekleyen mail görev taslağı bulunmuyor.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {pendingDrafts.map((draft) => {
                const edit = draftEdits[draft.id] || buildInitialEdit(draft);

                return (
                  <div
                    key={draft.id}
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: 16,
                      background: colors.card2,
                      padding: 14
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 1000, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {draft.subject || draft.title || "Mailden gelen görev"}
                        </div>
                        <div style={{ marginTop: 3, color: colors.muted, fontSize: 11, fontWeight: 850 }}>
                          İleten: {draft.forwardedByName || draft.forwardedByEmail || "-"} · Gönderen: {draft.fromEmail || "-"}
                        </div>
                      </div>
                      <button type="button" onClick={() => archiveDraft(draft)} title="Taslağı arşivle" style={{ ...secondaryButtonStyle, padding: "8px 10px" }}>
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr", gap: 10 }}>
                      <label style={labelStyle}>
                        Görev Başlığı
                        <input value={edit.title} onChange={(event) => updateDraftEdit(draft.id, "title", event.target.value)} style={inputStyle} />
                      </label>
                      <label style={labelStyle}>
                        Süreç Projesi
                        <select value={edit.projectId} onChange={(event) => updateDraftEdit(draft.id, "projectId", event.target.value)} style={inputStyle}>
                          {activeProjects.map((project) => (
                            <option key={project.id} value={project.id}>{project.name}</option>
                          ))}
                        </select>
                      </label>
                      <label style={labelStyle}>
                        Firma
                        <input
                          list="mail-task-company-options"
                          value={edit.companyName}
                          onChange={(event) => updateDraftEdit(draft.id, "companyName", event.target.value)}
                          placeholder="Firma yoksa boş kalabilir"
                          style={inputStyle}
                        />
                      </label>
                      <label style={labelStyle}>
                        Sorumlu
                        <input
                          list="mail-task-user-options"
                          value={edit.responsibleName}
                          onChange={(event) => updateDraftEdit(draft.id, "responsibleName", event.target.value)}
                          style={inputStyle}
                        />
                      </label>
                      <label style={labelStyle}>
                        Tarih
                        <input
                          type="date"
                          value={edit.dueDate || ""}
                          onChange={(event) => updateDraftEdit(draft.id, "dueDate", event.target.value)}
                          min={todayInputValue()}
                          style={inputStyle}
                        />
                      </label>
                      <label style={labelStyle}>
                        Öncelik
                        <select value={edit.priority} onChange={(event) => updateDraftEdit(draft.id, "priority", event.target.value)} style={inputStyle}>
                          <option value="düşük">Düşük</option>
                          <option value="normal">Normal</option>
                          <option value="yüksek">Yüksek</option>
                          <option value="acil">Acil</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                      <label style={labelStyle}>
                        Not / Mail İçeriği
                        <textarea
                          rows={5}
                          value={edit.note}
                          onChange={(event) => updateDraftEdit(draft.id, "note", event.target.value)}
                          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }}
                        />
                      </label>
                      <label style={labelStyle}>
                        Görev Adımları
                        <textarea
                          rows={5}
                          value={edit.stepsText}
                          onChange={(event) => updateDraftEdit(draft.id, "stepsText", event.target.value)}
                          placeholder={"Boş kalırsa varsayılan 3 adım oluşur.\nHer satır bir adım."}
                          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.45 }}
                        />
                      </label>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 12 }}>
                      <div style={{ color: colors.muted, fontSize: 11, fontWeight: 850 }}>
                        Etiketler: {[
                          draft.tagCompany && `firma=${draft.tagCompany}`,
                          draft.tagResponsible && `sorumlu=${draft.tagResponsible}`,
                          draft.tagDueDate && `tarih=${draft.tagDueDate}`,
                          draft.tagPriority && `öncelik=${draft.tagPriority}`,
                          draft.tagNotifyAll && "#herkes"
                        ].filter(Boolean).join(" · ") || "Etiket yok"}
                      </div>
                      <button type="button" onClick={() => convertDraftToTask(draft)} style={buttonStyle}>
                        Göreve Çevir
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <datalist id="mail-task-company-options">
        {firmaListesi.map((company) => (
          <option key={company.firmaAdi || company.name} value={company.firmaAdi || company.name} />
        ))}
      </datalist>
      <datalist id="mail-task-user-options">
        {users.map((item) => (
          <option key={item.uid || item.id || item.email} value={item.name || item.displayName || item.email} />
        ))}
      </datalist>
    </div>
  );
}

export default MailTasksView;
